import React from "react";
import "./CaseStudy.css";
import { useNavigate } from "react-router-dom";

export default function SonosApp() {
  const navigate = useNavigate();

  return (
    <div className="case-page">

      <button className="case-back" onClick={() => navigate(-1)}>
        <svg viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6.5 11.5L2.5 7.5M2.5 7.5L6.5 3.5M2.5 7.5H12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Back to Projects
      </button>

      <div className="case-hero">
        <h1>Sonos App <em>Redesign</em></h1>
        <p className="case-hero-intro">
          A full end-to-end redesign of the Sonos mobile app, applying Jesse James Garrett's Five Elements of User Experience to resolve a fragmented ecosystem and create a unified, premium control experience.
        </p>
        <div className="case-meta-inline">
          <span><strong>Role</strong>UX/UI Designer</span>
          <span><strong>Duration</strong>2 Weeks</span>
          <span><strong>Tools</strong>Figma</span>
          <span><strong>Framework</strong>Five Elements of UX</span>
        </div>
      </div>

      <div className="case-hero-img has-image">
        <img src="/images/sonosCover.png" alt="Sonos App Redesign hero" />
      </div>

      <div className="case-body">

        {/* ── 01 Overview ── */}
        <div className="case-section">
          <span className="case-section-num">01 — Overview</span>
          <h2>Project <em>Overview</em></h2>
          <p>This report outlines the process of developing a new user experience for the Sonos mobile app, following Jesse James Garrett's Five Elements of User Experience. The report aims to address significant usability issues that have frustrated users and tarnished the brand's reputation since a major app update. The redesign process will start with a foundational understanding of user needs and market analysis and move to the creation of low- and high-fidelity wireframes.</p>
          <p>The Sonos app is the primary interface for controlling Sonos smart speakers and sound systems. Sonos has a reputation for providing premium, high-quality sound, offering multi-room audio control. The app allows users to connect to a large number of music platforms and stream from one app. However, the app has been criticized for performance issues, an unintuitive user interface, and a lack of clear navigation.</p>
          <p>This redesign seeks to incorporate core functionality and ease of use, prioritizing stability, performance, and a user-centric design that aligns with the premium quality of Sonos hardware. The new app will provide a stable platform for managing multi-room audio and create a visually clean interface that is both functional and enjoyable to use.</p>
        </div>

        <div className="case-divider" />

        {/* ── 02 Strategy ── */}
        <div className="case-section">
          <span className="case-section-num">02 — Strategy</span>
          <h2>Research &amp; <em>Strategy</em></h2>
          <p>The strategy element, the first of Garret's five planes, helped define the who, what, and why behind the Sonos app redesign. To answer these questions and shape the project's goals, I conducted user research, analyzed competing products, and identified the core issues driving user frustration. The goal is to redesign the existing app to not only fill the market need for a multi-room audio controller but also to address the specific pain points of existing Sonos users.</p>

          <h3>User Research &amp; Competitor Analysis</h3>
          <p>A review of user feedback on forums like the official Sonos Community and app store reviews reveals a consistent pattern of frustration with the current app. Common complaints include:</p>
          <ul>
            <li><strong>Fragmented Ecosystem and Confusing Navigation:</strong> The existence of two separate apps (Sonos and S1) creates a disjointed user experience.</li>
            <li><strong>Missing Core Features:</strong> The app lacks essential functions, such as playlist creation and effective speaker management.</li>
            <li><strong>Poor Usability:</strong> The interface is often described as unintuitive and clunky because of its lack of clear navigation.</li>
          </ul>

          <div className="case-img-row">
            <div className="case-img">
              <img src="/images/sonosS1app.png" alt="Sonos S1 App" />
            </div>
            <div className="case-img">
              <img src="/images/sonosOriginalApp.png" alt="Sonos Original App" />
            </div>
          </div>

          <p>A deeper analysis of the Sonos ecosystem reveals a fundamental usability flaw that goes beyond the app's star rating: the existence of two separate apps, Sonos and S1. This dual-app system forces users with older speakers to use a separate app, which creates a fragmented and confusing experience. This is one of the key usability issues driving user dissatisfaction, as it prevents products from being grouped together. This creates a disjointed user experience and is a contradiction of the brand's promise of seamless audio control.</p>
          <p>To further illustrate the usability challenges within the Sonos ecosystem, Figures 1 and 2 show the Sonos app and the S1 app. These figures visually highlight the significant differences in the user interface and overall design between the two apps, specifically the Home and Now Playing screens. The two apps lack consistency in navigation and core functionality.</p>

          <div className="case-callout">"The existence of two separate apps — Sonos and S1 — creates a disjointed user experience that contradicts the brand's promise of seamless audio control."</div>

          <h3>Competitor Analysis</h3>
          <table className="case-table">
            <thead>
              <tr>
                <th>Competitor</th>
                <th>Apple App Store Rating</th>
                <th>Google Play Rating</th>
                <th>Strengths</th>
                <th>Weaknesses</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Denon HEOS</strong></td>
                <td>4.4</td>
                <td>3.9</td>
                <td>Broad streaming integration; Flexible Multi-Room Control</td>
                <td>Inconsistent performance; Busy and Unintuitive UI</td>
              </tr>
              <tr>
                <td><strong>Sony Music Center</strong></td>
                <td>4.6</td>
                <td>4.5</td>
                <td>Unified Ecosystem managing a wide range of Sony products; Device Customization</td>
                <td>Outdated UI; Confusing navigation and not intuitively organized</td>
              </tr>
              <tr>
                <td><strong>BluOS</strong></td>
                <td>4.7</td>
                <td>4.3</td>
                <td>High Resolution Audio; Stable + Reliable connection to speakers</td>
                <td>Complex Interface that prioritizes technical functionality over user experience</td>
              </tr>
            </tbody>
          </table>

          <p>The competitor analysis shows that while other apps may have more features or higher ratings, they still struggle with a combination of stability, intuitive design, and comprehensive features, which are the same problems that have plagued the Sonos app. This confirms that addressing these core issues is the top priority for the Sonos redesign and for addressing market needs.</p>
          <p>The new Sonos app will differentiate itself by being highly focused on the control of a multi-room system, while still integrating seamlessly with content providers. My redesign will incorporate comprehensive features, prioritize performance, and create a user experience that matches the premium quality of the hardware. This approach will not only address the issues with the current app but also set a new standard for reliability that the competition has yet to achieve.</p>
        </div>

        <div className="case-divider" />

        {/* ── 03 Scope ── */}
        <div className="case-section">
          <span className="case-section-num">03 — Scope</span>
          <h2>Requirements &amp; <em>Scope</em></h2>
          <p>The scope defines the functional and content requirements of the app. For this redesign, I will focus on two key screens: the <strong>Home Screen</strong> and the <strong>Now Playing Screen</strong>. The requirements below will be applied universally to these screens to ensure a cohesive experience.</p>

          <h3>Content</h3>
          <p>Before going into the screen-specific requirements, this section covers the overall brand elements, including the color palette, typography, and iconography.</p>

          <h3>Color Palette</h3>
          <p>The chosen color palette uses a dark mode as the primary theme, with a selected accent color. The primary colors are black (#000000), dark gray (#323537), and light gray (#474B4F), which provide a high-contrast, modern, and visually appealing backdrop that makes album art stand out. This color palette also reduces eye strain, a benefit for users who interact with the app in low-light environments. A blue accent (#4188FF) will be used for interactive elements to provide clear visual cues and a sense of premium quality.</p>
          <div className="case-img">
            <img src="/images/sonosColors.png" alt="Sonos color palette" />
          </div>

          <h3>Typography</h3>
          <p>To maintain a clean and legible appearance, I will use a single modern sans-serif font family. <strong>Avenir Next</strong> is a suitable choice for its clean lines and excellent readability across various weights and sizes. It projects a sense of modernity and sophistication that aligns with the Sonos brand.</p>
          <div className="case-img">
            <img src="/images/sonosText.png" alt="Sonos typography" />
          </div>

          <h3>Iconography</h3>
          <p>The app's iconography will be minimalist with clean lines to maintain a modern feel. Icons will be used for primary navigation and interactive elements, ensuring they are easily recognizable and accessible at first glance. For example, the home icon will represent "Home," a star icon for "My Sonos," a magnifying glass for "Search," and a gear icon for "Settings". The blue accent color will be used to highlight the active icon on the navigation bar, providing clear visual feedback for the user.</p>
          <div className="case-img">
            <img src="/images/sonosIcons.png" alt="Sonos iconography" />
          </div>

          <h3>Home Screen Interface</h3>
          <p>The Home Screen will adhere to the following content requirements:</p>
          <ul>
            <li><strong>Color Palette:</strong> primarily dark gray and black with blue accents.</li>
            <li><strong>Typography:</strong> Avenir Next in various weights.</li>
            <li><strong>Navigation Bar:</strong> a horizontal bar at the bottom of the screen with clear icons for Home, Search, My Sonos, and Settings.</li>
            <li><strong>Content:</strong> the main display area will feature a scrollable list of "Your System" for speaker management, recently played music, and My Sonos Playlists. Large, tappable album art and clear text will be used for all items.</li>
          </ul>

          <h3>Now Playing Screen Interface</h3>
          <p>The Now Playing Screen will adhere to the following content requirements:</p>
          <ul>
            <li><strong>Color Palette:</strong> dynamic, with the background color subtly reflecting the album art to create an immersive feel. The controls will be in the primary dark gray with blue accents for interactive elements.</li>
            <li><strong>Typography:</strong> Avenir Next, with song and artist names prominently displayed.</li>
            <li><strong>Content:</strong> a large, high-resolution image of the album art will dominate the top of the screen. The song title and artist will be below it. Playback controls (Play/Pause, Skip, etc.) and a volume slider will be at the bottom.</li>
            <li><strong>Icons:</strong> icons for speaker grouping, queue management, and a more menu will be clear and easily accessible.</li>
          </ul>
        </div>

        <div className="case-divider" />

        {/* ── 04 Structure ── */}
        <div className="case-section">
          <span className="case-section-num">04 — Structure</span>
          <h2>Interface &amp; <em>Architecture</em></h2>
          <p>The structure of the app is divided into Interface Design and Information Architecture, which determine how the app responds to user interactions and how content is organized.</p>

          <h3>Interface Design</h3>
          <p>The redesigned app prioritizes clear visual feedback and intuitive navigation to address the core usability problems of the Sonos app. The interface is designed to be responsive and effectively display state changes.</p>

          <h3>Home Screen Interface</h3>
          <ul>
            <li><strong>State of Change:</strong> When a user taps a button on the navigation bar, it will change color to the accent blue to indicate the current screen. When a song is playing, the Now Playing bar will appear at the bottom.</li>
            <li><strong>Navigation:</strong> The bottom navigation bar provides a consistent point of access to the app's main sections (Home, Search, My Sonos, Settings) without getting lost.</li>
          </ul>

          <h3>Now Playing Screen Interface</h3>
          <ul>
            <li><strong>Interaction:</strong> The use of a three-dot icon reveals an overlay menu for advanced actions like adding a playlist or setting sleep time. A back button provides a second form of navigation, giving users a reliable way to return to the previous screen.</li>
          </ul>

          <h3>Information Architecture</h3>
          <p>The information architecture prioritizes the most important content and functions based on user needs.</p>

          <h3>Home Screen</h3>
          <ul>
            <li><strong>Prioritization:</strong> The home screen is designed to be a dashboard that gives users a quick overview of what's currently playing and easy access to their most used content.</li>
            <li><strong>Organization:</strong> Content is organized into clear, scannable sections: "Your System," "Recently Played," and "My Sonos Playlists". This structure helps users find what they need without extensive searching.</li>
          </ul>

          <h3>Now Playing Screen</h3>
          <ul>
            <li><strong>Prioritization:</strong> This screen is designed around the simple, primary goal of controlling the current playback. The album art and playback controls are given the most visual weight.</li>
            <li><strong>Organization:</strong> Information is organized hierarchically. The most prominent information (song title, artist, queuing, and menu) is at the top, followed by the controls.</li>
          </ul>
        </div>

        <div className="case-divider" />

        {/* ── 05 Skeleton ── */}
        <div className="case-section">
          <span className="case-section-num">05 — Skeleton</span>
          <h2>Low-Fidelity <em>Wireframes</em></h2>
          <p>The skeleton determines the visual presentation and arrangement of content. I created two low-fidelity wireframes to visualize the proposed designs.</p>

          <div className="case-img-row">
            <div className="case-img">
              <img src="/images/sonoshomewireframe.png" alt="Home Screen low-fidelity wireframe" />
            </div>
            <div className="case-img">
              <img src="/images/sonosNPwireframe.png" alt="Now Playing Screen low-fidelity wireframe" />
            </div>
          </div>

          <h3>Home Screen — Low-Fidelity Wireframe</h3>
          <ul>
            <li><strong>Interface Design:</strong> The home screen features a clean layout with large tappable areas for speaker management, album art, and text. The top section is reserved for the speaker status and controls. The body of the screen is a scrollable list of recent plays and playlists.</li>
            <li><strong>Navigation Design:</strong> The bottom navigation bar is present at all times, with clear icons for Home, Search, and My Sonos. The user can easily move between these core sections.</li>
            <li><strong>Information Design:</strong> The design uses a clear hierarchy. The pinned speakers system is at the top, and all subsequent items are organized in a grid for easy scanning.</li>
          </ul>

          <h3>Now Playing Screen — Low-Fidelity Wireframe</h3>
          <ul>
            <li><strong>Interface Design:</strong> The "Now Playing" screen is dominated by the album art. The playback controls are large and centrally located for easy access. The volume slider is a prominent horizontal bar.</li>
            <li><strong>Navigation Design:</strong> A back button at the top-left returns the user to the previous screen. The speaker grouping and queue management icons are placed on both sides of the song title and artist, making them easy to find.</li>
            <li><strong>Information Design:</strong> The design prioritizes the most critical information: what's playing. The song title and artist are clearly displayed above the controls, and all other information is secondary.</li>
          </ul>
        </div>

        <div className="case-divider" />

        {/* ── 06 Surface ── */}
        <div className="case-section">
          <span className="case-section-num">06 — Surface</span>
          <h2>High-Fidelity <em>Designs</em></h2>
          <p>The surface is the final visual design. I created high-fidelity wireframes that show the finished look and feel of the two screens.</p>

          <div className="case-img-row">
            <div className="case-img">
              <img src="/images/sonosHomehf.png" alt="Home Screen high-fidelity design" />
            </div>
            <div className="case-img">
              <img src="/images/sonosNPmenu.png" alt="Now Playing Screen high-fidelity design" />
            </div>
          </div>

          <h3>Home Screen — High-Fidelity Wireframe</h3>
          <p>The home screen uses a black background with white text and blue accents. The album art and Sonos Playlists are prominently displayed and easily accessible in a scrollable view. The navigation bar at the bottom uses a dark gray background and blue accents for interactive elements, such as play/pause and advanced speaker management.</p>

          <h3>Now Playing Screen — High-Fidelity Wireframe</h3>
          <p>The "Now Playing" screen features a background color reflecting the album art to create a highly immersive and dynamic experience. Next to the song title is a three-dot button that opens an overlay menu for additional actions. The controls are clean and minimalist, with blue accents used for interactive elements and active icons. A back arrow in the top left corner provides a secondary form of navigation to direct users back to the previous page.</p>
        </div>

        <div className="case-divider" />

        {/* ── 07 Rationale ── */}
        <div className="case-section">
          <span className="case-section-num">07 — Rationale</span>
          <h2>Design &amp; Functionality <em>Rationale</em></h2>
          <p>My design choices are a direct response to the usability issues identified in the strategic research.</p>

          <h3>The Usability Gap</h3>
          <p>The market is in need of a reliable and intuitive control experience that matches the premium Sonos hardware. While the Sonos app has a high rating on the app store, a deep dive into recent reviews and community forums reveals significant user frustrations. These pain points are particularly from long-time customers who feel the app does not match the quality of the hardware. The lower Google Play rating (3.9 stars) serves as an indicator of this widespread dissatisfaction. The separate Sonos S1 app used only for older speakers is a usability flaw that creates a confusing experience for users and further demonstrates the need for a unified solution. This redesign is an opportunity to fill a gap that even competitors have yet to address.</p>

          <h3>Color and Typography</h3>
          <p>I maintained the Sonos dark theme and added a blue accent to create a modern look. The use of color guides the user's eye and makes the interface feel more responsive and intuitive, a key aspect of good design.</p>

          <h3>Consistent Navigation</h3>
          <p>The persistent bottom navigation is a core design choice based on the principle of consistency. This ensures users can always access the main sections of the app from any screen without getting lost or confused. This directly addresses the feedback that the old app's navigation was unintuitive.</p>
        </div>

        <div className="case-divider" />

        {/* ── 08 Conclusion ── */}
        <div className="case-section">
          <span className="case-section-num">08 — Conclusion</span>
          <h2>Final <em>Thoughts</em></h2>
          <p>This UX strategy report outlines a comprehensive plan to redesign the Sonos app, transforming it from a source of frustration into an intuitive and visually appealing audio controller. By applying Jesse James Garrett's five planes of user experience, I went from a foundational understanding of the problem to a detailed, user-centric design solution. The redesign focuses on incorporating core functionality and creating an interface that is seamless and modern. The new app's purpose is to seamlessly integrate with the Sonos ecosystem, ensuring that the control software maintains the same quality as its hardware. This redesign will not only fix the current issues but will also help rebuild user trust and solidify Sonos's position as a leader in premium home audio.</p>
        </div>

        <div className="case-divider" />

        {/* ── 09 At a Glance ── */}
        <div className="case-section">
          <span className="case-section-num">09 — At a Glance</span>
          <h2>Project <em>Snapshot</em></h2>
          <div className="outcome-grid">
            <div className="outcome-cell">
              <div className="outcome-number">5</div>
              <div className="outcome-desc">Elements of UX framework applied end-to-end</div>
            </div>
            <div className="outcome-cell">
              <div className="outcome-number">2wk</div>
              <div className="outcome-desc">Full redesign turnaround from research to high-fidelity</div>
            </div>
            <div className="outcome-cell">
              <div className="outcome-number">3</div>
              <div className="outcome-desc">Competitors analyzed across the smart audio category</div>
            </div>
            <div className="outcome-cell">
              <div className="outcome-number">2</div>
              <div className="outcome-desc">Core screens fully redesigned (Home + Now Playing)</div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
